import React, { useRef, useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { generateOTP } from "../../requests";
import { notifyError, notifySuccess } from "../../utils/helper";

const ConfirmDeleteModal = ({ show, onHide, email, requestId, onSubmit }) => {
    const [otp, setOtp] = useState(new Array(6).fill(""));
    const inputRefs = useRef([]);
    const [timer, setTimer] = useState(0);
    const timerRef = useRef(null);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (show) {
            handleSendOtp();
        }
        return () => clearTimeout(timerRef.current);
    }, [show]);

    useEffect(() => {
        if (timer > 0) {
            timerRef.current = setTimeout(() => setTimer(timer - 1), 1000);
        }
        return () => clearTimeout(timerRef.current);
    }, [timer]);

    const handleChange = (element, index) => {
        const value = element.value.replace(/[^0-9]/g, "");
        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleBackspace = (e, index) => {
        if (e.key === "Backspace" && index > 0 && !otp[index]) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleSendOtp = async () => {
        setIsSendingOtp(true);
        try {
            await generateOTP(requestId);
            setTimer(60);
            notifySuccess("OTP sent successfully");
        } catch (err) {
            notifyError("Failed to send OTP");
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleSubmit = async () => {
        const code = otp.join("");
        if (code.length !== 6) {
            notifyError("Please enter a 6-digit OTP");
            return;
        }
        setIsSubmitting(true);
        try {
            if (onSubmit) {
                await onSubmit(code);
            }
            setOtp(new Array(6).fill(""));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered dialogClassName="custom-modal-width">
            <Modal.Body
                className="confirm-delete-modal"
                style={{ padding: "32px", display: "flex", flexDirection: "column", gap: "32px" }}
            >
                {/* Header */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <h5 style={{ fontWeight: "600", fontSize: "24px", margin: 0 }}>PractAIce</h5>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "24px" }}>🔒</span>
                        <h5 style={{ fontWeight: "600", fontSize: "24px", margin: 0 }}>Confirm Delete</h5>
                    </div>
                    <p style={{ color: "#6B7280", fontSize: "16px", margin: 0 }}>
                        Please enter the 6-digit authentication code sent to {email}
                    </p>
                </div>

                {/* OTP input */}
                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                        {otp.map((data, index) => (
                            <input
                                key={index}
                                type="text"
                                maxLength="1"
                                className="otp-input"
                                value={otp[index]}
                                onChange={(e) => handleChange(e.target, index)}
                                onKeyDown={(e) => handleBackspace(e, index)}
                                ref={(ref) => (inputRefs.current[index] = ref)}
                                disabled={isSubmitting}
                            />
                        ))}
                    </div>

                    {/* Resend OTP */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: "14px",
                            color: "#6B7280",
                        }}
                    >
                        <span>Code sent to: {email}</span>
                        <span
                            role="button"
                            style={{
                                cursor: timer > 0 || isSendingOtp ? "not-allowed" : "pointer",
                                opacity: timer > 0 || isSendingOtp ? 0.6 : 1,
                            }}
                            onClick={timer === 0 && !isSendingOtp ? handleSendOtp : undefined}
                        >
                            {isSendingOtp
                                ? "Sending..."
                                : timer > 0
                                    ? `Send Again (${timer}s)`
                                    : "Send Again?"}
                        </span>
                    </div>
                </div>

                {/* Actions */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "12px",
                        marginTop: "8px",
                    }}
                >
                    <button
                        onClick={onHide}
                        className="confirm-modal-btn-outline"
                        disabled={isSubmitting || isSendingOtp}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="confirm-modal-btn"
                        disabled={isSubmitting || isSendingOtp}
                    >
                        {isSubmitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            "Delete data"
                        )}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default ConfirmDeleteModal;

